// Emulation of the signal path between the Taiko Controller and Taiko no
// Tatsujin Nijiiro, so the web tester classifies a hit exactly the way the
// arcade build would.
//
// The chain being reproduced, verified against the shipped binaries:
//
//   ESP32-S3 firmware  ->  a hit drives one zone's HID axis with an envelope
//                          for output_hold_samples (200 scans at 10416.667
//                          scans/s, i.e. ~19.2 ms), reported at 1 kHz. The
//                          envelope is seeded from the onset peak, droops
//                          slightly each scan and is held up by the zone's
//                          live level, so the value keeps changing and the
//                          event outlasts a 60 fps frame. Only the strongest
//                          zone of a player is non-zero; Don drives the axis
//                          positive, Ka negative.
//
//   TaikoArcadeLoader  ->  bnusio_GetAnalogIn() with analog_input = true
//                          returns (u16)(32768 * axis) and treats anything
//                          <= 100 as silence. The axis snapshot comes from
//                          SDL, which normalises the descriptor's -127..127
//                          logical range onto -32768..32767, so the float is
//                          axis / 127. The read happens once per presented
//                          frame (bnusio::Update runs from the DXGI Present
//                          hook), i.e. at the config.toml fpslimit.
//
//   Taiko.exe          ->  the TAIKO TEST menu stores eight 1..100 settings
//                          (Don/Ka x weak/strong x 1P/2P) and converts each
//                          one to a raw comparison level with
//                          setting * 0xFFFF / 100, so the settings are a
//                          percentage of the USIO board's full 16-bit range.
//
// Because TaikoArcadeLoader only spans half of the 16-bit range, a full
// strength hit on the controller reads as 50% to the game. That is not a bug
// in this emulation: it is what makes the stock 5 / 25 defaults feel right,
// and it is why analog input was scaled down from 65535 in the loader.

// ---------------------------------------------------------------------------
// Channels
// ---------------------------------------------------------------------------

// Zone order used by the waveforms, one block of four per player.
export const ZONE_LEFT_DON = 0;
export const ZONE_RIGHT_DON = 1;
export const ZONE_LEFT_KA = 2;
export const ZONE_RIGHT_KA = 3;

export const CHANNELS_PER_PLAYER = 4;
export const PLAYER_COUNT = 2;
export const CHANNEL_COUNT = CHANNELS_PER_PLAYER * PLAYER_COUNT;

export const DON = "don";
export const KA = "ka";

const ZONE_SURFACE = [DON, DON, KA, KA];

// A threshold group is one row of the TAIKO TEST menu: the game keeps a single
// weak/strong pair per player per surface, shared by that surface's left and
// right sensors.
export const thresholdGroupKey = (player, surface) => `${player + 1}p-${surface}`;

export const channelPlayer = (channel) => Math.floor(channel / CHANNELS_PER_PLAYER);
export const channelZone = (channel) => channel % CHANNELS_PER_PLAYER;
export const channelSurface = (channel) => ZONE_SURFACE[channelZone(channel)];
export const channelGroupKey = (channel) =>
    thresholdGroupKey(channelPlayer(channel), channelSurface(channel));

export const THRESHOLD_GROUP_KEYS = [];
for (let player = 0; player < PLAYER_COUNT; player++) {
    THRESHOLD_GROUP_KEYS.push(thresholdGroupKey(player, DON));
    THRESHOLD_GROUP_KEYS.push(thresholdGroupKey(player, KA));
}

// ---------------------------------------------------------------------------
// Scales
// ---------------------------------------------------------------------------

// Logical maximum of the arcade report descriptor's signed axes.
export const HID_AXIS_MAX = 127;
// Full scale bnusio_GetAnalogIn() reaches in analog input mode.
export const LOADER_ANALOG_FULL_SCALE = 32768;
// Values at or below this are discarded by the loader before the game sees them.
export const LOADER_ANALOG_DEADZONE = 100;
// Full scale the game's threshold conversion (setting * 0xFFFF / 100) assumes.
export const USIO_FULL_SCALE = 65535;

// Highest percentage the controller can present to the game, i.e. 50.
export const MAX_REACHABLE_PERCENT = (LOADER_ANALOG_FULL_SCALE * 100) / USIO_FULL_SCALE;

// Reports arrive evenly spaced in device time, so a report's index in the
// stream is a far better clock than the moment its event handler happened to
// run: Chrome dispatches HID reports in bursts when the page is busy, which
// would collapse a 19 ms hold into near-zero apparent width and make the poll
// grid step over hits the game would have caught.
//
// The spacing itself has to be measured rather than assumed. The firmware aims
// for one report per millisecond, but taiko_usb_send drops a report when the
// endpoint is still busy instead of queueing it, so the delivered rate can sit
// well under the timer rate. Averaging arrivals over a window long enough to
// swallow burst jitter recovers the true spacing.
export const NOMINAL_REPORT_INTERVAL_MS = 1;
const CADENCE_WINDOW_MS = 500;
const CADENCE_MIN_HZ = 50;
const CADENCE_MAX_HZ = 4000;

// The frame rate bnusio::Update runs at; matches config.toml's default fpslimit.
export const DEFAULT_POLL_RATE_HZ = 120;
export const POLL_RATE_CHOICES = [30, 60, 120, 144, 240];

// Seconds of drum time a waveform spans. Deriving the sample count from the
// poll rate keeps the window a fixed duration, so changing the emulated frame
// rate changes how finely the second is sampled rather than how much of it
// fits on screen.
export const WINDOW_SECONDS = 1;
export const windowSizeForPollRate = (hz) =>
    Math.max(2, Math.round(hz * WINDOW_SECONDS));

/** Signed HID axis -> the u16 bnusio_GetAnalogIn() hands to the game. */
export const loaderAnalogFromAxis = (axis) => {
    const magnitude = Math.abs(axis);
    if (magnitude <= 0) {
        return 0;
    }
    const scaled = Math.min(
        LOADER_ANALOG_FULL_SCALE,
        Math.floor((LOADER_ANALOG_FULL_SCALE * magnitude) / HID_AXIS_MAX)
    );
    return scaled > LOADER_ANALOG_DEADZONE ? scaled : 0;
};

/** TAIKO TEST setting (1..100) -> raw level, as Taiko.exe computes it. */
export const thresholdRawLevel = (setting) =>
    Math.trunc((setting * USIO_FULL_SCALE) / 100);

/** Raw level -> the percentage the TAIKO TEST settings are expressed in. */
export const rawLevelToPercent = (raw) => (raw * 100) / USIO_FULL_SCALE;

// ---------------------------------------------------------------------------
// Thresholds
// ---------------------------------------------------------------------------

// Ranges and defaults come straight from Data/x64/testmode/TestMode.xml, where
// the weak rows are min="1" max="99" default="5" and the strong rows are
// min="2" max="100" default="25". The hidden upper-limit / lower-limit items
// are what keep weak strictly below strong.
export const WEAK_MIN = 1;
export const WEAK_MAX = 99;
export const WEAK_DEFAULT = 5;
export const STRONG_MIN = 2;
export const STRONG_MAX = 100;
export const STRONG_DEFAULT = 25;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

export const defaultThresholds = () =>
    THRESHOLD_GROUP_KEYS.reduce((groups, key) => {
        groups[key] = { weak: WEAK_DEFAULT, strong: STRONG_DEFAULT };
        return groups;
    }, {});

/**
 * Apply one edit while keeping the pair legal the way the menu does: the weak
 * row can never reach the strong row, and the strong row can never fall to it.
 */
export const withThresholdEdit = (groups, key, field, value) => {
    const group = groups[key];
    if (!group) {
        return groups;
    }
    const next =
        field === "weak"
            ? {
                weak: clamp(Math.round(value), WEAK_MIN, Math.min(WEAK_MAX, group.strong - 1)),
                strong: group.strong,
            }
            : {
                weak: group.weak,
                strong: clamp(Math.round(value), Math.max(STRONG_MIN, group.weak + 1), STRONG_MAX),
            };
    return { ...groups, [key]: next };
};

/** Repair thresholds coming back from storage so a stale shape cannot break the page. */
export const sanitizeThresholds = (groups) => {
    const defaults = defaultThresholds();
    if (!groups || typeof groups !== "object") {
        return defaults;
    }
    return THRESHOLD_GROUP_KEYS.reduce((sanitized, key) => {
        const stored = groups[key];
        const strong = clamp(Math.round(Number(stored?.strong)) || STRONG_DEFAULT, STRONG_MIN, STRONG_MAX);
        const weak = clamp(Math.round(Number(stored?.weak)) || WEAK_DEFAULT, WEAK_MIN, Math.min(WEAK_MAX, strong - 1));
        sanitized[key] = { weak, strong };
        return sanitized;
    }, defaults);
};

// ---------------------------------------------------------------------------
// Hit outcomes
// ---------------------------------------------------------------------------

export const HIT_NONE = 0;
// Registered above the weak threshold but below the strong one: a small note.
export const HIT_LIGHT = 1;
// Registered at or above the strong threshold: the level that feeds a big note.
export const HIT_HARD = 2;
// The firmware drove a hit hard enough to register, but the game's frame poll
// fell either side of the hold and never sampled it.
export const HIT_MISSED = 3;

// ---------------------------------------------------------------------------
// Emulator
// ---------------------------------------------------------------------------

/**
 * Rolling window of what the game would have seen, fed by the raw 1 kHz HID
 * stream and sampled at the emulated frame rate.
 *
 * Reports are replayed against a wall-clock poll grid rather than sampled once
 * per animation frame, so a hold that falls between two frames is dropped here
 * exactly as it would be dropped in-game.
 */
export const createEmulator = ({ pollRateHz = DEFAULT_POLL_RATE_HZ } = {}) => {
    let windowSize = windowSizeForPollRate(pollRateHz);
    const channels = [];
    for (let i = 0; i < CHANNEL_COUNT; i++) {
        channels.push({
            // Percentage the game saw at each poll, oldest sample first.
            samples: new Float32Array(windowSize),
            // Outcome recorded at the poll the hit landed on, HIT_* above.
            markers: new Uint8Array(windowSize),
            lastPolledRaw: 0,
            // Pulse tracking on the raw report stream, one latch from the firmware.
            pulseActive: false,
            pulsePeakAxis: 0,
            pulseObserved: false,
            pulseRegistered: false,
            counts: { light: 0, hard: 0, soft: 0, missed: 0 },
            lastPercent: 0,
            // Reports the axis stayed non-zero, i.e. milliseconds of hold.
            pulseReports: 0,
            lastHoldMs: 0,
        });
    }

    let writeIndex = 0;
    let pollPeriodMs = 1000 / pollRateHz;
    // Both in milliseconds of device time, counted in reports received.
    let clockMs = 0;
    let nextPollMs = 0;
    let msPerReport = NOMINAL_REPORT_INTERVAL_MS;
    let cadenceReports = 0;
    let cadenceStartMs = null;
    const heldAxes = new Int8Array(CHANNEL_COUNT);
    let thresholdLevels = null;

    const resolveLevels = (thresholds) => {
        const levels = new Array(CHANNEL_COUNT);
        for (let channel = 0; channel < CHANNEL_COUNT; channel++) {
            const group = thresholds[channelGroupKey(channel)];
            levels[channel] = {
                weak: thresholdRawLevel(group.weak),
                strong: thresholdRawLevel(group.strong),
            };
        }
        return levels;
    };

    const closePulse = (channel) => {
        const state = channels[channel];
        if (!state.pulseActive) {
            return;
        }
        if (!state.pulseRegistered) {
            const peakRaw = loaderAnalogFromAxis(state.pulsePeakAxis);
            const wouldRegister = peakRaw >= thresholdLevels[channel].weak;
            if (!wouldRegister) {
                state.counts.soft++;
            } else if (!state.pulseObserved) {
                state.counts.missed++;
                state.markers[writeIndex] = HIT_MISSED;
            }
        }
        state.lastHoldMs = state.pulseReports * msPerReport;
        state.pulseActive = false;
        state.pulsePeakAxis = 0;
        state.pulseReports = 0;
        state.pulseObserved = false;
        state.pulseRegistered = false;
    };

    /** Take one frame's worth of samples using the axis state in force right now. */
    const poll = () => {
        writeIndex = (writeIndex + 1) % windowSize;
        for (let channel = 0; channel < CHANNEL_COUNT; channel++) {
            const state = channels[channel];
            const raw = loaderAnalogFromAxis(heldAxes[channel]);
            const percent = rawLevelToPercent(raw);
            state.samples[writeIndex] = percent;
            state.markers[writeIndex] = HIT_NONE;
            state.lastPercent = percent;

            const { weak, strong } = thresholdLevels[channel];
            if (state.pulseActive) {
                state.pulseObserved = true;
            }
            // The game registers on the rising edge across the weak threshold;
            // the firmware holds one constant value for the whole latch, so a
            // single latch can only ever produce one note.
            if (raw >= weak && state.lastPolledRaw < weak) {
                const hard = raw >= strong;
                state.markers[writeIndex] = hard ? HIT_HARD : HIT_LIGHT;
                if (hard) {
                    state.counts.hard++;
                } else {
                    state.counts.light++;
                }
                state.pulseRegistered = true;
            }
            state.lastPolledRaw = raw;
        }
    };

    /** Fold one HID input report into the pulse tracking. */
    const applyReport = (axes) => {
        for (let channel = 0; channel < CHANNEL_COUNT; channel++) {
            const axis = axes[channel];
            const state = channels[channel];
            if (axis > 0) {
                if (!state.pulseActive) {
                    state.pulseActive = true;
                    state.pulsePeakAxis = axis;
                    state.pulseReports = 0;
                    state.pulseObserved = false;
                    state.pulseRegistered = false;
                } else if (axis > state.pulsePeakAxis) {
                    state.pulsePeakAxis = axis;
                }
                state.pulseReports++;
            } else {
                closePulse(channel);
            }
            heldAxes[channel] = axis;
        }
    };

    return {
        channels,
        get writeIndex() {
            return writeIndex;
        },
        setPollRate(hz) {
            pollPeriodMs = 1000 / hz;
            nextPollMs = clockMs;
            const nextWindow = windowSizeForPollRate(hz);
            if (nextWindow === windowSize) {
                return;
            }
            // The trace is a fixed second, so a different rate needs a
            // different sample count. History cannot be resampled honestly, so
            // start the window empty rather than stretching what came before.
            windowSize = nextWindow;
            writeIndex = 0;
            channels.forEach((state) => {
                state.samples = new Float32Array(windowSize);
                state.markers = new Uint8Array(windowSize);
            });
        },
        setThresholds(thresholds) {
            thresholdLevels = resolveLevels(thresholds);
        },
        resetCounts() {
            channels.forEach((state) => {
                state.counts.light = 0;
                state.counts.hard = 0;
                state.counts.soft = 0;
                state.counts.missed = 0;
            });
        },
        /** Reports per second actually arriving from the device. */
        get reportRateHz() {
            return 1000 / msPerReport;
        },
        /**
         * Replay every buffered report in order, stepping the emulated game
         * poll across them. Each entry is the eight decoded axes, and `nowMs`
         * is a wall clock reading for this batch.
         *
         * Within a batch, time comes from the report count rather than any
         * per-report timestamp, so burst delivery cannot distort a hit. The
         * spacing those counts are worth is calibrated from batch arrivals
         * across a long window, which keeps the emulated poll grid anchored to
         * real time and therefore to the game's real frame rate. The array is
         * consumed in place.
         */
        advance(reports, nowMs) {
            if (!thresholdLevels) {
                reports.length = 0;
                return;
            }
            if (typeof nowMs === "number") {
                if (cadenceStartMs === null) {
                    cadenceStartMs = nowMs;
                }
                cadenceReports += reports.length;
                const elapsed = nowMs - cadenceStartMs;
                if (elapsed >= CADENCE_WINDOW_MS) {
                    const hz = (cadenceReports * 1000) / elapsed;
                    // A backgrounded tab drops reports on the floor, which
                    // would otherwise calibrate to a nonsense rate.
                    if (hz >= CADENCE_MIN_HZ && hz <= CADENCE_MAX_HZ) {
                        msPerReport = 1000 / hz;
                    }
                    cadenceStartMs = nowMs;
                    cadenceReports = 0;
                }
            }
            for (let i = 0; i < reports.length; i++) {
                while (nextPollMs <= clockMs) {
                    poll();
                    nextPollMs += pollPeriodMs;
                }
                applyReport(reports[i]);
                clockMs += msPerReport;
            }
            reports.length = 0;
        },
    };
};

/**
 * Split an arcade mode report body into the eight zone axes. Don drives its
 * player's axis positive and Ka negative, and the firmware only ever latches
 * one zone per player, so at most one entry per block of four is non-zero.
 */
export const decodeArcadeAxes = (view, out) => {
    for (let player = 0; player < PLAYER_COUNT; player++) {
        const base = player * CHANNELS_PER_PLAYER;
        const horizontal = view.getInt8(player * 2);
        const vertical = view.getInt8(player * 2 + 1);
        out[base + ZONE_LEFT_DON] = horizontal > 0 ? horizontal : 0;
        out[base + ZONE_LEFT_KA] = horizontal < 0 ? -horizontal : 0;
        out[base + ZONE_RIGHT_DON] = vertical > 0 ? vertical : 0;
        out[base + ZONE_RIGHT_KA] = vertical < 0 ? -vertical : 0;
    }
    return out;
};
